import { LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import EmblaCarousel from "embla-carousel";
import ons from "onsenui";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { isDefined } from "./Generic/IsDefined";

interface CancelableCallback<T> {
  cancelable: true;
  callback: (result: T | undefined) => void;
}

interface UncancelableCallback<T> {
  cancelable: false;
  callback: (result: T) => void;
}

type DatepickerCallback<T> = CancelableCallback<T> | UncancelableCallback<T>;

interface ParamsTime {
  type: "time";
  buttonText?: string;
  callbackObj: DatepickerCallback<LocalTime>;
  defaultDate?: LocalDateTime;
}

interface ParamsDate {
  type: "date";
  buttonText?: string;
  callbackObj: DatepickerCallback<LocalDate>;
  defaultDate?: LocalDateTime;
  maxDate?: LocalDate;
  minDate?: LocalDate;
}

interface ParamsDateAndTime {
  type: "datetime";
  buttonText?: string;
  callbackObj: DatepickerCallback<LocalDateTime>;
  defaultDate?: LocalDateTime;
}

type DatepickerParams = ParamsTime | ParamsDate | ParamsDateAndTime;

export function openDatepicker(params: DatepickerParams, title: string) {
  const elem = document.createElement("div");
  elem.style.position = "absolute";
  elem.style.top = "0";
  elem.style.bottom = "0";
  elem.style.left = "0";
  elem.style.right = "0";
  elem.style.zIndex = "5";
  elem.style.overflow = "hidden";

  const originalCallback = params.callbackObj.callback;

  params.callbackObj.callback = (a: any) => {
    const result = (originalCallback as any)(a);

    ReactDOM.unmountComponentAtNode(elem);

    if (elem.parentElement !== null) {
      elem.parentElement.removeChild(elem);
    }

    return result;
  };

  ReactDOM.render(
    React.createElement(MedflytDatepicker, {
      params: params,
      title: title,
    }),
    elem
  );

  document.body.appendChild(elem);
}

interface MedflytDatepickerProps {
  params: DatepickerParams;
  title: string;
  buttonText?: string;
  defaultDate?: LocalDateTime;
}

interface MedflytDatepickerState {
  result: LocalDateTime;
  currentViewedMonth: number;
}

const MONTH_MAP = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAYS_MAP = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"];

const HOURS_MAP = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

export class MedflytDatepicker extends React.Component<
  MedflytDatepickerProps,
  MedflytDatepickerState
> {
  private timeCarouselRef: null | HTMLDivElement = null;
  private embla: null | EmblaCarousel = null;

  constructor(props: MedflytDatepickerProps) {
    super(props);

    const nowLocalDateTime =
      props.params.defaultDate !== undefined
        ? props.params.defaultDate
        : LocalDateTime.now().withHour(1).withMinute(0).withSecond(0).withNano(0);
    this.state = {
      result: nowLocalDateTime,
      currentViewedMonth: nowLocalDateTime.monthValue(),
    };
  }

  componentDidMount() {
    ons.disableDeviceBackButtonHandler();
    document.addEventListener("backbutton", this.cancel);

    if (this.getIsTimeEnabled()) {
      setTimeout(() => {
        // Casting is done here because react guarantees refs are set before componentDidMount
        this.embla = EmblaCarousel(this.timeCarouselRef as HTMLDivElement, {
          loop: true,
          startIndex: 0,
        });
        this.embla.on("select", () => {
          if (this.embla !== null) {
            this.setHour(HOURS_MAP[this.embla.selectedScrollSnap()]);
          }
        });
      }, 0);
    }
  }

  componentWillUnmount() {
    ons.enableDeviceBackButtonHandler();
    document.removeEventListener("backbutton", this.cancel);
  }

  getIsDateEnabled() {
    return this.props.params.type === "date" || this.props.params.type === "datetime";
  }

  getIsTimeEnabled() {
    return this.props.params.type === "time" || this.props.params.type === "datetime";
  }

  fillDaysFromLastMonth(amount: number): JSX.Element[] {
    const result = [];

    for (let i = amount; i > 0; i--) {
      result.push(
        <div className="MedflytDatepicker_dateGridDay" key={"day-last-" + i}>
          <span style={{ color: "#CACDD1" }}>
            {this.state.result.minusMonths(1).month().maxLength() + 1 - i}
          </span>
        </div>
      );
    }

    return result;
  }

  fillDaysFromNextMonth(amount: number): JSX.Element[] {
    const result = [];
    for (let i = 1; i <= amount; i++) {
      result.push(
        <div className="MedflytDatepicker_dateGridDay" key={"day-next-" + i}>
          <span style={{ color: "#CACDD1" }}>{i}</span>
        </div>
      );
    }

    return result;
  }

  getMaxDate() {
    const params = this.props.params;
    if (params.type === "date" && isDefined(params.maxDate)) {
      return params.maxDate;
    }
    return null;
  }

  getMinDate() {
    const params = this.props.params;
    if (params.type === "date" && isDefined(params.minDate)) {
      return params.minDate;
    }
    return null;
  }

  getCalendarDays(): JSX.Element[] {
    const maxDate = this.getMaxDate();
    const minDate = this.getMinDate();
    const dayName = this.state.result
      .withMonth(this.state.currentViewedMonth)
      .withDayOfMonth(1)
      .dayOfWeek()
      .name();
    const dayFirstTwoLetters =
      dayName.substring(0, 1) + dayName.substring(1, 2).toLocaleLowerCase();
    const daysToFillFromLastMonth = DAYS_MAP.indexOf(dayFirstTwoLetters);

    let result = this.fillDaysFromLastMonth(daysToFillFromLastMonth);

    for (let i = 1; i <= this.state.result.month().maxLength(); i++) {
      try {
        const currDate = this.state.result
          .withMonth(this.state.currentViewedMonth)
          .withDayOfMonth(i);
        const isAfterOrEqualMinDate =
          !isDefined(minDate) || !currDate.toLocalDate().isBefore(minDate);
        const isBeforeOrEqualMaxDate =
          !isDefined(maxDate) || !currDate.toLocalDate().isAfter(maxDate);
        const isBetweenMinAndMaxDates = isAfterOrEqualMinDate && isBeforeOrEqualMaxDate;

        if (isBetweenMinAndMaxDates) {
          result.push(this.getActiveCalendarDay(currDate));
        } else {
          result.push(this.getInactiveCalendarDay(currDate));
        }
      } catch (e) {
        if (e.name !== "DateTimeException") {
          throw e;
        }
      }
    }

    result = result.concat(this.fillDaysFromNextMonth(42 - result.length));
    return this.splitIntoRows(result);
  }

  getActiveCalendarDay(date: LocalDateTime) {
    const isSelectedDate =
      date.toLocalDate().toString() === this.state.result.toLocalDate().toString();
    return (
      <div
        className={
          "MedflytDatepicker_dateGridDay " +
          (isSelectedDate ? "MedflytDatepicker_dateGridSelectedDay" : "")
        }
        onClick={() => this.setState({ result: date })}
        key={"day-" + date.dayOfMonth()}
      >
        <span>{date.dayOfMonth()}</span>
      </div>
    );
  }

  getInactiveCalendarDay(date: LocalDateTime) {
    return (
      <div className="MedflytDatepicker_dateGridDay" key={"day-" + date.dayOfMonth()}>
        <span style={{ color: "#CACDD1" }}>{date.dayOfMonth()}</span>
      </div>
    );
  }

  splitIntoRows(days: JSX.Element[]): JSX.Element[] {
    const result = [];

    let tempDaysArr = days;

    while (tempDaysArr.length !== 0) {
      const daysInRow = tempDaysArr.slice(0, 7);
      tempDaysArr = tempDaysArr.slice(7);

      result.push(
        <div key={"row-" + tempDaysArr} className="MedflytDatepicker_dateGridDayRow">
          {daysInRow.map((curr) => curr)}
        </div>
      );
    }

    return result;
  }

  getCyclicMonth(month: number) {
    return ((((month - 1) % 12) + 12) % 12) + 1;
  }

  getCyclicHour(hour: number) {
    return ((hour % 23) + 23) % 23;
  }

  viewNextMonth() {
    const newMonth = this.getCyclicMonth(this.state.currentViewedMonth + 1);
    const newYear = newMonth === 1 ? this.state.result.year() + 1 : this.state.result.year();

    this.setState({ currentViewedMonth: newMonth, result: this.state.result.withYear(newYear) });
  }

  viewLastMonth() {
    const newMonth = this.getCyclicMonth(this.state.currentViewedMonth - 1);
    const newYear = newMonth === 12 ? this.state.result.year() - 1 : this.state.result.year();

    this.setState({ currentViewedMonth: newMonth, result: this.state.result.withYear(newYear) });
  }

  isAm(hour: number) {
    return hour < 12;
  }

  isPm(hour: number) {
    return hour >= 12;
  }

  selectAm() {
    if (this.isPm(this.state.result.hour())) {
      this.setState({ result: this.state.result.withHour(this.state.result.hour() - 12) });
    }
  }

  selectPm() {
    if (this.isAm(this.state.result.hour())) {
      this.setState({ result: this.state.result.withHour(this.state.result.hour() + 12) });
    }
  }

  setHour(hour: number) {
    if (!this.isHourSelected(hour)) {
      this.setState({
        result: this.state.result.withHour(
          this.isPm(this.state.result.hour()) ? (hour % 12) + 12 : hour % 12
        ),
      });
    }
  }

  isHourSelected(hour: number) {
    return this.state.result.hour() % 12 === hour % 12;
  }

  emblaSelectIndex(index: number) {
    if (this.embla !== null) {
      this.embla.scrollTo(index);
    }
  }

  submit() {
    if (this.props.params.type === "date") {
      this.props.params.callbackObj.callback(this.state.result.toLocalDate());
    } else if (this.props.params.type === "time") {
      this.props.params.callbackObj.callback(this.state.result.toLocalTime());
    } else {
      this.props.params.callbackObj.callback(this.state.result);
    }
  }

  cancel = () => {
    if (this.props.params.callbackObj.cancelable) {
      (this.props.params.callbackObj.callback as (a: undefined) => void)(undefined);
    }
  };

  render() {
    return (
      <div className="MedflytDatepicker">
        <div className="MedflytDatepicker_backdrop" onClick={() => this.cancel()} />
        <div className="MedflytDatepicker_main">
          {this.props.title !== "" ? (
            <span className="MedflytDatepicker_title">{this.props.title}</span>
          ) : (
            ""
          )}
          {this.getIsDateEnabled() ? (
            <div className="MedflytDatepicker_date">
              <div className="MedflytDatepicker_topBar">
                <div className="MedflytDatepicker_currentMonth">
                  {MONTH_MAP[
                    this.state.result.withMonth(this.state.currentViewedMonth).month().ordinal()
                  ] +
                    " " +
                    this.state.result.year()}
                </div>
                <div className="MedflytDatepicker_arrowControlsContainer">
                  <div
                    className="MedflytDatepicker_arrowControlLeft"
                    onClick={() => this.viewLastMonth()}
                  />
                  <div
                    className="MedflytDatepicker_arrowControlRight"
                    onClick={() => this.viewNextMonth()}
                  />
                </div>
              </div>
              <div className="MedflytDatepicker_dateGrid">
                <div className="MedflytDatepicker_dateGridDayRow">
                  {DAYS_MAP.map((curr) => (
                    <div key={"day-" + curr} className="MedflytDatepicker_dateGridDayHeaders">
                      <span>{curr}</span>
                    </div>
                  ))}
                </div>
                {this.getCalendarDays()}
              </div>
            </div>
          ) : (
            ""
          )}
          {this.getIsTimeEnabled() ? (
            <div className="MedflytDatepicker_date">
              <div className="MedflytDatepicker_topBar">
                <div className="MedflytDatepicker_currentMonth">{translate(STRINGS.TIME)}</div>
                <div className="MedflytDatepicker_timeAmPm">
                  <span
                    className={
                      this.isAm(this.state.result.hour())
                        ? "MedflytDatepicker_timeAmPmSelected"
                        : ""
                    }
                    onClick={() => this.selectAm()}
                    style={{ marginRight: 16 }}
                  >
                    AM
                  </span>
                  <span
                    className={
                      this.isPm(this.state.result.hour())
                        ? "MedflytDatepicker_timeAmPmSelected"
                        : ""
                    }
                    onClick={() => this.selectPm()}
                  >
                    PM
                  </span>
                </div>
              </div>
              <div
                className="MedflytDatepicker_timeCarouselContainer"
                ref={(ref) => (this.timeCarouselRef = ref)}
              >
                <div className="MedflytDatepicker_timeCarousel">
                  {HOURS_MAP.map((curr, index) => (
                    <div
                      key={"hour-" + curr}
                      className={
                        "MedflytDatepicker_timeHour " +
                        (this.isHourSelected(curr) ? "MedflytDatepicker_timeHourSelected" : "")
                      }
                      onClick={() => this.emblaSelectIndex(index)}
                    >
                      {`${curr}:00`}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            ""
          )}
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              justifyContent: "space-around",
              width: "100%",
            }}
          >
            <div className="MedflytDatepicker_submitButton" onClick={() => this.submit()}>
              {this.props.params.buttonText !== undefined
                ? this.props.params.buttonText
                : translate(STRINGS.SUBMIT)}
            </div>
            {this.props.params.callbackObj.cancelable ? (
              <>
                <span style={{ width: 15 }} />
                <div className="MedflytDatepicker_cancelButton" onClick={() => this.cancel()}>
                  {translate(STRINGS.CANCEL)}
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    );
  }
}
